import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

import {
  messages,
  ALL_EVENT,
  LOGIN_FORM,
  EMPTY_STRING,
  localStorageKeys,
  ADD_RECAPZ_STEPS,
  RECAP_FORM_FIELDS,
  eventBookingStatus,
  eventBookingConstants,
  numberConstants,
} from "../../../../constants";
import {
  getLocalStorageItem,
  saveToLocalStorage,
  removeItemFromStorage,
} from "../../../../system";
import BackButton from "../../../../shared/components/IconButton/BackButton";
import {
  customToast,
  safeJsonParse,
  filterByStatus,
  removeDraftEvents,
  buildRecapBriefPayload,
  hasEmptyReelDetailDescription,
} from "../../../../shared";
import Modal from "../../../../container/Views/Login/Modal";
import { eventBookingActions } from "../../../../redux/actions";
import AiSummaryRecap from "../AddRecapz/AiSummaryRecap";
import SelectRecapType from "../AddRecapz/SelectRecapType";
import SelectEventQuantity from "../AddRecapz/SelectEventQuantity";

const AddRecapz = (props) => {
  const { t } = useTranslation("common");

  const { addRecapz, onNext, isAddUpdateRecap, isUpdateRecap } = props;

  const [activeStep, setActiveStep] = useState(() => {
    return Number(
      getLocalStorageItem(
        localStorageKeys.ADD_RECAPZ_ACTIVE_STEP,
        ADD_RECAPZ_STEPS.ADD_BOOKING
      )
    );
  });
  const [currentContent, setCurrentContent] = useState(EMPTY_STRING);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Matches the opening <h5> tag plus any existing "N. " prefix right after it.
  const HEADING_PREFIX_REGEX = /(<h5[^>]*>)\s*(?:\d+\.\s*)?/g;
  const isAddFlow = isAddUpdateRecap && !isUpdateRecap;

  useEffect(() => {
    window.scrollTo(0, 0);
    saveToLocalStorage(localStorageKeys.ADD_RECAPZ_ACTIVE_STEP, activeStep);
    // eslint-disable-next-line
  }, [activeStep]);

  useEffect(() => {
    if (
      props.activeTab === eventBookingConstants.TABS_ID.ADD_REEL &&
      Array.isArray(props.formState)
    ) {
      let updatedAddRecapz = [...addRecapz];

      // Only 1 event (remove All Event reel)
      if (
        !props.isEditEvent &&
        removeDraftEvents(
          filterByStatus(props.formState ?? [], [eventBookingStatus.CANCEL])
        ).length === numberConstants.ONE
      ) {
        updatedAddRecapz = updatedAddRecapz.filter(
          (reel) => reel.eventId !== ALL_EVENT.id
        );
      }

      // Remove recapz for draft or canceled events
      const excludedEventIds = props.formState
        ?.filter(
          (event) =>
            event.isSaveAsDraft || event.status === eventBookingStatus.CANCEL
        )
        .map((event) => event.id);

      if (excludedEventIds.length > numberConstants.ZERO) {
        updatedAddRecapz = updatedAddRecapz.filter(
          (reel) => !excludedEventIds.includes(reel.eventId)
        );
      }
      const eventNameMap = new Map(
        props.formState?.map((event) => [event.id, event.eventName])
      );

      // Update eventName from formState
      updatedAddRecapz = updatedAddRecapz.map((reel) => {
        const eventName =
          eventNameMap.get(reel.eventId) ??
          (reel.eventId === -numberConstants.ONE
            ? ALL_EVENT.name
            : reel.eventName);

        return {
          ...reel,
          eventName,
          reelDetail: reel?.reelDetail?.map((detail, index) => ({
            ...detail,
            reelName: `${eventName} - Recap ${index + numberConstants.ONE}`,
          })),
        };
      });

      // Set updated recapz if something changed
      if (JSON.stringify(updatedAddRecapz) !== JSON.stringify(addRecapz)) {
        props.setAddRecapz(updatedAddRecapz);
      }
    }
    // eslint-disable-next-line
  }, [props.activeTab, props.formState, addRecapz]);

  useEffect(() => {
    if (
      addRecapz.length === numberConstants.ZERO &&
      activeStep !== ADD_RECAPZ_STEPS.SELECT_EVENTS_QUANTITY
    ) {
      setActiveStep(ADD_RECAPZ_STEPS.ADD_BOOKING);
    }
    // eslint-disable-next-line
  }, [addRecapz]);

  const recapBriefs = useMemo(() => {
    return (
      addRecapz?.map((recap) => {
        const reelDetailsList = recap.reelDetail ?? [];

        const reelDetail = isAddFlow
          ? (reelDetailsList.find((reel) => reel?.isAddFlowReel === true) ?? {})
          : (reelDetailsList[numberConstants.ZERO] ?? {});
        return {
          ...reelDetail,
          eventName: recap.eventName || EMPTY_STRING, // Safely attach event name
        };
      }) ?? []
    );
  }, [addRecapz]);

  useEffect(() => {
    const currentDescription = recapBriefs;
    setCurrentContent(currentDescription);
  }, [recapBriefs]);

  const handlePrevious = () => {
    props.onPrevious();
  };

  const handleSkip = () => {
    if (promptLoginIfNeeded()) return;

    props.setAddRecapz([]);
    removeItemFromStorage(localStorageKeys.ADD_CANCELLED_RECAPZ);
    setActiveStep(ADD_RECAPZ_STEPS.ADD_BOOKING);
    onNext();
  };

  const handleActiveStep = (step) => {
    if (promptLoginIfNeeded()) return;

    setActiveStep(step);
  };

  const promptLoginIfNeeded = () => {
    if (!props.isAuthenticated) {
      setIsModalOpen(true);
      return true;
    }

    return false;
  };

  const getVisibleReelDetail = (item) => {
    if (!isAddFlow) return item.reelDetail ?? [];
    return (item.reelDetail ?? []).filter((r) => r.isAddFlowReel === true);
  };

  const generateEditBrief = () => {
    let dataToSend = [];

    // Case 1: recaps tied to formState events
    dataToSend = props.formState.reduce((acc, event) => {
      const matchedRecap = addRecapz.find(
        (recap) => recap.eventId === event.id
      );
      if (matchedRecap) {
        const filteredReelDetail = getVisibleReelDetail(matchedRecap);
        acc.push(
          buildRecapBriefPayload(
            { ...matchedRecap, reelDetail: filteredReelDetail },
            props.formState,
            event
          )
        );
      }
      return acc;
    }, []);

    // Case 2: combined reels not tied to formState
    addRecapz
      .filter((recap) => recap.isCombinedEventsReel === true)
      .forEach((combined) => {
        const filteredReelDetail = getVisibleReelDetail(combined);
        if (filteredReelDetail.length > 0) {
          dataToSend.push(
            buildRecapBriefPayload(
              { ...combined, reelDetail: filteredReelDetail },
              props.formState,
              null
            )
          );
        }
      });

    props.getEditBrief(dataToSend, onResponseCallback);
  };

  const onResponseCallback = (response) => {
    if (response) {
      // Safely parse AI response into JSON, fallback to empty array if invalid JSON
      const parsed = safeJsonParse(response, []);
      const recapDescriptionMap = parsed.reduce((acc, item) => {
        const key = Object.keys(item)[0];
        const value = item[key];

        // merge text if key already exists, otherwise assign
        acc[key] = acc[key] ? acc[key] + " " + value : value;
        return acc;
      }, {});

      // Renumbers every <h5> heading in a description, in order.
      // startNumber = null strips numbering entirely.
      // Returns the updated text plus the next available number, so callers
      // can keep counting across multiple details/headings.
      const renumberHtmlDescription = (description, startNumber) => {
        if (!description) return { text: description, nextNumber: startNumber };

        let counter = startNumber;
        const text = description.replace(
          HEADING_PREFIX_REGEX,
          (_match, openTag) => {
            if (counter == null) return openTag;
            const numbered = `${openTag}${counter}. `;
            counter += 1;
            return numbered;
          }
        );

        return { text, nextNumber: counter };
      };

      const countHeadings = (description) =>
        (description?.match(/<h5[^>]*>/g) || []).length;

      const updatedAddRecapz = addRecapz.map((recap) => {
        const details = recap.reelDetail || [];
        const oldCount = details.filter((d) => d.isAddFlowReel !== true).length;
        const hasNew = details.some((d) => d.isAddFlowReel === true);

        const shouldRenumber = isAddFlow && oldCount > 0 && hasNew;

        // If exactly 1 old reel, it becomes "1." and new reels start at 2.
        // If >1 old reel, old is left untouched and new starts right after the count.
        let nextNumber = oldCount === 1 ? 2 : oldCount + 1;

        return {
          ...recap,
          reelDetail: details.map((detail) => {
            // Pre-existing reel
            if (isAddFlow && detail.isAddFlowReel !== true) {
              if (shouldRenumber && oldCount === 1) {
                const { text } = renumberHtmlDescription(
                  detail[RECAP_FORM_FIELDS.DESCRIPTION],
                  1
                );
                return { ...detail, [RECAP_FORM_FIELDS.DESCRIPTION]: text };
              }
              return detail; // oldCount > 1 (or not add flow): don't touch
            }

            // New (add-flow) reel
            const rawDescription =
              recapDescriptionMap[
                recap.eventId === -numberConstants.ONE
                  ? -numberConstants.ONE
                  : recap.eventName
              ] ?? "";

            if (!shouldRenumber) {
              // No old reels: number 1,2.. if this description has multiple
              // headings, otherwise leave unnumbered.
              const { text } = renumberHtmlDescription(
                rawDescription,
                countHeadings(rawDescription) > 1 ? 1 : null
              );
              return { ...detail, [RECAP_FORM_FIELDS.DESCRIPTION]: text };
            }

            const { text, nextNumber: updatedNext } = renumberHtmlDescription(
              rawDescription,
              nextNumber
            );
            //nextNumber = updatedNext; // carry counter to the next new detail

            return { ...detail, [RECAP_FORM_FIELDS.DESCRIPTION]: text };
          }),
        };
      });

      props.setAddRecapz(updatedAddRecapz);
      handleActiveStep(ADD_RECAPZ_STEPS.RECAP_FORM);
    }
  };

  const handleBriefChange = (content, index) => {
    const updatedAddRecapz = addRecapz.map((recap, i) => {
      if (i === index) {
        return {
          ...recap,
          reelDetail:
            recap.reelDetail?.map((detail) => {
              // In the add-recap flow, only update reels added through this
              // session (isAddFlowReel === true). Pre-existing reels keep
              // whatever description they already had.
              if (isAddFlow && detail.isAddFlowReel !== true) return detail;

              return {
                ...detail,
                [RECAP_FORM_FIELDS.DESCRIPTION]:
                  content?.[RECAP_FORM_FIELDS.DESCRIPTION],
              };
            }) || [],
        };
      }
      return recap; //keep others unchanged
    });

    props.setAddRecapz(updatedAddRecapz);
  };

  const handleBriefSubmit = () => {
    if (hasEmptyReelDetailDescription(addRecapz)) {
      customToast(messages.EDIT_BRIEF_MESSAGE, toast.TYPE.ERROR);
      return;
    }
    //setActiveStep(ADD_RECAPZ_STEPS.SELECT_EVENTS_QUANTITY);
    onNext();
  };

  useEffect(() => {
    if (isAddUpdateRecap) {
      setActiveStep(ADD_RECAPZ_STEPS.SELECT_EVENTS_QUANTITY);
    }
  }, [isAddUpdateRecap]);

  return (
    <>
      <div
        className={`add-recapz-container mt-4 mx-3 ${
          activeStep === ADD_RECAPZ_STEPS.RECAP_FORM
            ? "add-recapz-custom"
            : EMPTY_STRING
        }`}
      >
        <div className="row">
          {activeStep === ADD_RECAPZ_STEPS.ADD_BOOKING && (
            <div className="add-booking col-12 col-md-8 col-lg-6 margin-0-auto">
              <p className="add-recapz-heading label-content-600 mb-2">
                would you like to add video edit(s) for your bookings?
              </p>
              <p className="add-recapz-description mb-0">
                let our editors turn your shutterb captured video clips into an
                event recap starting at $108/edit.
              </p>
              <div className="mt-4">
                <button
                  type="button"
                  className="button-common button-fill yes-btn mb-3 mb-lg-0"
                  onClick={() =>
                    handleActiveStep(ADD_RECAPZ_STEPS.SELECT_EVENTS_QUANTITY)
                  }
                >
                  {t("yes")}
                </button>
                <button
                  type="button"
                  className="button-common button-outline ml-4 mb-3 mb-lg-0"
                  onClick={handleSkip}
                >
                  {t("skip")}
                </button>
              </div>
            </div>
          )}
          {activeStep === ADD_RECAPZ_STEPS.SELECT_EVENTS_QUANTITY && (
            <SelectEventQuantity
              handleActiveStep={handleActiveStep}
              formState={removeDraftEvents(
                filterByStatus(props.formState ?? [], [
                  eventBookingStatus.CANCEL,
                ])
              )}
              isAddUpdateRecap={isAddUpdateRecap}
              isUpdateRecap={isUpdateRecap}
            />
          )}
          {activeStep === ADD_RECAPZ_STEPS.SELECT_RECAP_TYPE && (
            <SelectRecapType
              handleActiveStep={handleActiveStep}
              generateEditBrief={generateEditBrief}
              isAddFlow={isAddFlow}
            />
          )}
          {activeStep === ADD_RECAPZ_STEPS.RECAP_FORM && (
            <AiSummaryRecap
              currentRecaps={recapBriefs}
              handleBriefChange={handleBriefChange}
              handleValidSubmit={handleBriefSubmit}
              handleGoBack={() =>
                handleActiveStep(ADD_RECAPZ_STEPS.SELECT_RECAP_TYPE)
              }
              generateEditBrief={generateEditBrief}
              currentContent={currentContent}
              setCurrentContent={setCurrentContent}
              isAddFlow={isAddFlow}
            />
          )}
        </div>
        {!isAddUpdateRecap && (
          <BackButton
            className="event-back-button"
            onClick={handlePrevious}
            text={`${props.previousActiveTab}`}
          />
        )}
      </div>

      {!props.isAuthenticated && isModalOpen && (
        <Modal
          showBackButton={false}
          formType={LOGIN_FORM}
          setIsModalOpen={setIsModalOpen}
          isAllowModal
        />
      )}
    </>
  );
};

// Mapping the component's props to the reducer's state
const mapStateToProps = (state) => ({
  addRecapz: state.eventBooking.addRecapz,
  formState: state.eventBooking.formState,
  isEditEvent: state.eventBooking.isEditEvent,
  previousActiveTab: state.eventBooking.previousActiveTab,
  isAuthenticated: state.authentication.isAuthenticated,
});

// Mapping the component's props to the related actions
const mapDispatchToProps = (dispatch) => ({
  setAddRecapz: (data) => dispatch(eventBookingActions.setAddRecapz(data)),
  getEditBrief: (data, onResponseCallback) =>
    dispatch(eventBookingActions.getEditBrief(data, onResponseCallback)),
});

// mapping action and store the function via props
export default connect(mapStateToProps, mapDispatchToProps)(AddRecapz);
